import moment from 'moment'
import { memo, useEffect, useMemo, useState } from 'react'
import { IoIosCloseCircleOutline } from 'react-icons/io'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useShallow } from 'zustand/react/shallow'
import { useTranslate } from '../../../../../hooks/language/use-translate'
import { useGetGamesBoxesRoomsDetails } from '../../../../../hooks/queries/games/games-boxes-rooms-details/use-get-games-boxes-rooms-details'
import { useEarningGameBoxRoom } from '../../../../../hooks/queries/games/games-boxes-rooms/use-earning-game-box-room'
import { useGetGameBoxRoomEarning } from '../../../../../hooks/queries/games/games-boxes-rooms/use-get-game-box-room-earning'
import { useGetGamesBoxesRooms } from '../../../../../hooks/queries/games/games-boxes-rooms/use-get-games-boxes-rooms'
import { useUpdateGameBoxRoom } from '../../../../../hooks/queries/games/games-boxes-rooms/use-update-game-box-room'
import type { GameBoxRoom } from '../../../../../services/games/games-boxes-rooms/types'
import { useGlobalStore } from '../../../../../store/global-store'
import { Observer } from '../../../../gifts/modal-gifts/components/gifts-container/components/observer'
import { Loading } from '../../../../gifts/modal-gifts/components/loading'
import { Timer } from '../../components/timer'
import { useCheckExpiredCoinDrop } from '../../use-check-expired-coin-drop'

interface Props {
  gameBoxRoom: GameBoxRoom
}

function Box({ gameBoxRoom }: Props) {
  const [isBoxOpened, setIsBoxOpenend] = useState(false)
  const [alreadyRescued, setAlreadyRescued] = useState(false)
  const [showLuckyEarned, setShowLuckyEarned] = useState(false)
  const [shouldHide, setShouldHide] = useState(false)

  const {
    allowRescue,
    setShouldAllowRescue,
    //
  } = useCheckExpiredCoinDrop(gameBoxRoom)

  const { gamesBoxesRoomsDetails, isLoading, hasNextPage, refetch, nextPage } =
    useGetGamesBoxesRoomsDetails({
      gameBoxRoomId: gameBoxRoom?.id,
    })

  const { translate } = useTranslate()
  const { getGameBoxRoomEarning } = useGetGameBoxRoomEarning()
  const { earningGameBoxRoom } = useEarningGameBoxRoom()
  const { update } = useUpdateGameBoxRoom()

  const rescueTime = useMemo(() => {
    return moment(gameBoxRoom?.dateLimitToRescue).format('YYYY-MM-DD HH:mm:ss')
  }, [gameBoxRoom?.dateLimitToRescue])

  async function handleRescue() {
    const check = await getGameBoxRoomEarning({
      gameBoxRoomId: gameBoxRoom?.id,
    })

    if (check?.error === 'already_received') {
      return setAlreadyRescued(true)
    }

    if (!check?.data) {
      const response = await earningGameBoxRoom({
        gameBoxRoomId: gameBoxRoom.id,
      })

      if (response) {
        setIsBoxOpenend(true)
      }
    }
  }

  function handleRemoveFromList() {
    update({ gameBoxRoomId: gameBoxRoom?.id, closed: true })
    setShouldHide(true)
  }

  useEffect(() => {
    if (gameBoxRoom && showLuckyEarned) {
      refetch()
    }
  }, [gameBoxRoom, showLuckyEarned, refetch])

  if (shouldHide) {
    return null
  }

  return (
    <div className="h-96 w-96 relative bg-gradient-to-b from-[#ffe0eb] to-white rounded-3xl p-6  flex flex-col items-center">
      {showLuckyEarned && (
        <div className="flex flex-col items-center flex-1">
          <p className="text-[#333] font-bold">
            {translate('modal-game-lucky-box-luck-all')}
          </p>

          <div className="flex flex-col overflow-y-scroll w-full mt-4 gap-4 h-1/2">
            {isLoading && <Loading />}

            {gamesBoxesRoomsDetails?.map((gameBoxRoomDetail) => (
              <div
                key={gameBoxRoomDetail?.id}
                className="w-full flex flex-row text-gray-900 items-center justify-between gap-2"
              >
                <div className="flex flex-row items-center gap-2">
                  <img
                    src={gameBoxRoomDetail?.user?.imgUser}
                    className="w-10 h-10 bg-black rounded-full"
                  />

                  <span className="text-xs font-bold">
                    {gameBoxRoomDetail?.user?.username}
                  </span>
                </div>

                <div className="flex flex-row px-2 gap-1 bg-amber-100 rounded-2xl items-center justify-center">
                  <span className="text-[8px]">🪙</span>

                  <span className="text-[8px] text-amber-600 font-bold">
                    {gameBoxRoomDetail?.valueEarning}
                  </span>
                </div>
              </div>
            ))}

            <Observer hasNextPage={hasNextPage} action={nextPage} />
          </div>
        </div>
      )}

      {isBoxOpened && (
        <div className="flex flex-col items-center justify-center flex-1">
          <p className="text-8xl">🎁</p>
          <p className="text-[#333]  font-semibold mt-4">
            {translate('modal-game-gold-miner-congrats')}
          </p>

          <div className="mt-10">
            <span className="text-amber-300 text-2xl font-bold">
              +
              {gameBoxRoom?.valueTotalDistribute /
                gameBoxRoom?.quantityPeopleEarning}
            </span>
          </div>

          <div className="absolute bottom-10">
            <span
              onClick={() => {
                setIsBoxOpenend(false)
                setShowLuckyEarned(true)
              }}
              className="text-[#ff7fa0] font-semibold text-lg"
            >
              {translate('modal-game-lucky-box-luck-all')}
            </span>
          </div>
        </div>
      )}

      {!isBoxOpened && !showLuckyEarned && (
        <>
          <h2 className="text-[#ff4f81] text-2xl font-extrabold mb-1">
            Coin Drop
          </h2>

          {allowRescue && (
            <Timer
              timeToEnd={rescueTime}
              className="text-[#ff4f81]"
              onFinish={handleRemoveFromList}
            />
          )}

          {gameBoxRoom.dateFinalDistribute && !allowRescue && (
            <Timer
              timeToEnd={gameBoxRoom.dateFinalDistribute}
              className="text-[#ff4f81]"
              onFinish={() => setShouldAllowRescue(true)}
            />
          )}

          <div className="flex flex-col items-center flex-1 justify-center w-full">
            <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white mb-3">
              <img
                src={gameBoxRoom?.user?.imgUser}
                alt="avatar"
                className="w-full h-full object-cover bg-black"
              />
            </div>

            <p className="text-[#333] text-base font-semibold">
              {translate('modal-game-lucky-value')}{' '}
              <span className="text-amber-300 font-bold">
                {gameBoxRoom?.valueTotalDistribute}
              </span>
            </p>
          </div>

          <div className="flex flex-col items-center justify-end">
            {!alreadyRescued && allowRescue && (
              <button
                className="bg-[#ff7fa0] hover:bg-[#ff6c95] px-10 py-3 rounded-full text-white font-semibold text-lg transition"
                onClick={handleRescue}
              >
                {translate('modal-game-lucky-open-box')}
              </button>
            )}

            {(alreadyRescued || !allowRescue) && (
              <button
                disabled
                className="bg-gray-400  px-10 py-3 rounded-full text-white font-semibold text-lg transition"
              >
                {translate(
                  alreadyRescued
                    ? 'modal-game-lucky-open-box-rescued'
                    : 'modal-game-lucky-open-box',
                )}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export const LuckyBoxModal = memo(() => {
  const { liveId } = useGlobalStore(
    useShallow((state) => ({
      liveId: state.liveId,
    })),
  )

  const { gamesBoxesRooms } = useGetGamesBoxesRooms({
    liveId: liveId!,
  })

  function closeModal() {
    if ('ReactNativeWebView' in window) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage('close-modal')
    }
  }

  const hasMoreThanOne = (gamesBoxesRooms?.length || 0) > 1

  return (
    <div className="w-screen h-screen flex items-center justify-center z-50 bg-black/50">
      <Swiper
        centeredSlides={hasMoreThanOne}
        slidesPerView={hasMoreThanOne ? 1.1 : 1}
        spaceBetween={hasMoreThanOne ? 20 : 0}
      >
        {gamesBoxesRooms?.map((gameBoxRoom) => (
          <SwiperSlide key={gameBoxRoom.id}>
            <Box gameBoxRoom={gameBoxRoom} />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="absolute text-white bottom-36" onClick={closeModal}>
        <IoIosCloseCircleOutline className="w-10 h-10" />
      </div>
    </div>
  )
})
